"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const PluginBase_1 = require("auto-widget-framework/src/Plugin/PluginBase");
const Mixin_1 = require("auto-widget-framework/src/Util/Mixin");
const IContainerInjection_1 = require("auto-widget-framework/src/Container/IContainerInjection");
const FormHandlerEvent_1 = require("./FormHandlerEvent");
const EventEmitter = require("events");
const FormStates_1 = require("../../Enum/FormStates");
const DataAttributes_1 = require("auto-widget-framework/src/Enum/DataAttributes");
const WidgetError_1 = require("auto-widget-framework/src/Error/WidgetError");
const FormValidatorTypes_1 = require("../../Enum/FormValidatorTypes");
const FormValidationRendererTypes_1 = require("../../Enum/FormValidationRendererTypes");
/**
 * Form handler base plugin.
 */
class FormHandlerPluginBase extends PluginBase_1.default {
    constructor(definition, formElement, formValidatorFactory, formValidationRendererFactory, scrollInView) {
        super(definition);
        this.initializedElements = [];
        this.formElement = formElement;
        this.state = FormStates_1.FormStates.EDITING;
        this.formValidatorFactory = formValidatorFactory;
        this.formValidationRendererFactory = formValidationRendererFactory;
        this.scrollInView = scrollInView;
    }
    /**
     * @inheritDoc
     */
    static create(definition, formElement, container) {
        return new this(definition, formElement, container.get("factory.form_validator"), container.get("factory.form_validation_renderer"), container.get("scroll_in_view"));
    }
    /**
     * Gets the form  DOM element.
     *
     * @return {HTMLFormElement}
     */
    getFormElement() {
        if (typeof this.formElement === "string") {
            const element = document.querySelector(this.formElement);
            if (element && element.tagName.toLocaleLowerCase() !== "form") {
                throw new WidgetError_1.default(`Invalid form element with ID "${this.formElement}".`);
            }
            return element;
        }
        return this.formElement;
    }
    /**
     * Gets the current state of the form.
     *
     * @return {string}
     */
    getState() {
        return this.state;
    }
    /**
     * Gets validation rules configuration for the form.
     */
    getValidationConfig() {
        return null;
    }
    /**
     * Gets the form validator.
     */
    getFormValidator() {
        if (this.getValidationConfig()) {
            return this.formValidatorFactory
                .create(FormValidatorTypes_1.FormValidatorTypes.BASE, {
                form: this.getFormElement(),
                config: this.getValidationConfig()
            });
        }
        else {
            return this.formValidatorFactory.create(FormValidatorTypes_1.FormValidatorTypes.NULL);
        }
    }
    /**
     * Gets the renderer for form errors.
     */
    getFormValidationRenderer() {
        if (this.getValidationConfig()) {
            return this.formValidationRendererFactory
                .create(FormValidationRendererTypes_1.FormValidationRendererTypes.BASE, {
                form: this.getFormElement(),
                config: this.getValidationConfig()
            });
        }
        else {
            return this.formValidationRendererFactory.create(FormValidationRendererTypes_1.FormValidationRendererTypes.NULL);
        }
    }
    /**
     * Initializes the form.
     * @return {FormHandlerPluginBase}
     */
    initialize() {
        this.registerSubmitButtonValidityDisplay();
        this.validateFormAndDisplaySubmit();
        this.initSubmitButtonEvents();
        this.enableLiveValidations();
        // Register form elements as initialized.
        this.initializedElements.push(this.getFormElement());
        for (let i = 0, element; element = this.getFormElement().elements[i++];) {
            this.initializedElements.push(element);
        }
        return this;
    }
    /**
     * Adds submit listeners.
     */
    initSubmitButtonEvents() {
        const formElement = this.getFormElement();
        this.filterRegisteredElements(formElement).forEach((form) => {
            //Handle validation and submission.
            form.addEventListener("submit", (event) => {
                event.preventDefault();
                this.doSubmit();
            });
        });
        // Handle action based submission.
        const actionButtons = this.filterRegisteredElements(formElement.querySelectorAll("[data-px-form-action]"));
        for (let i = 0, button; button = actionButtons[i]; ++i) {
            button.addEventListener("click", (event) => {
                // Set the action which submits.
                formElement.setAttribute("data-px-form-submit-action", button.getAttribute("data-px-form-action"));
                formElement.submit();
                event.preventDefault();
            });
            this.initializedElements.push(button);
        }
    }
    updateFormFields() {
        this.removeErrorMessagesForDisabledElements();
    }
    removeErrorMessagesForDisabledElements() {
        const validationRenderer = this.getFormValidationRenderer();
        const elements = this.getFormElement().elements;
        for (let i = 0, element; element = elements[i++];) {
            if (element.disabled && element.name) {
                validationRenderer.removeForField(element.name);
            }
        }
    }
    /**
     * Enables live validation and error display for fields.
     */
    enableLiveValidations() {
        const validate = (event) => {
            let field = event.target.name || event.currentTarget.name;
            if (field) {
                this.validateField(field, this.getValue(field));
            }
        };
        const elements = this.filterRegisteredElements();
        for (let i = 0, element; element = elements[i++];) {
            element.addEventListener("change", validate);
            element.addEventListener("focusout", validate);
            element.addEventListener("input", validate);
        }
    }
    /**
     * Return the form elements that weren't initialised with validation events.
     * @return {Array<HTMLFormElement>}
     */
    filterRegisteredElements(elements = null) {
        elements = elements || this.getFormElement().elements;
        if (elements.length === undefined || elements === this.getFormElement()) {
            elements = [elements];
        }
        let unregisteredElements = [];
        for (let i = 0, element; element = elements[i++];) {
            if (!this.initializedElements.includes(element)) {
                unregisteredElements.push(element);
            }
        }
        return unregisteredElements;
    }
    /**
     * Scrolls viewport in view of field with error.
     */
    scrollToFirstError() {
        //Scroll window to first error, but only when submission is started.
        if (this.getState() === FormStates_1.FormStates.VALIDATING) {
            const errorElement = this.getFormElement().querySelector(".is-invalid");
            if (errorElement && !this.scrollInView.isInView(errorElement)) {
                this.scrollInView.scrollInView(errorElement);
            }
        }
    }
    /**
     * Watch the form elements that need to be validated in order to go to the next step
     *
     * @param eventType
     */
    registerSubmitButtonValidityDisplay() {
        for (let i = 0, element; element = this.filterRegisteredElements()[i++];) {
            element.addEventListener("input", () => this.validateFormAndDisplaySubmit());
            element.addEventListener("change", () => this.validateFormAndDisplaySubmit());
        }
    }
    /**
     * Show the submit as disabled when the form is not valid or "clickable" when the form is valid.
     */
    validateFormAndDisplaySubmit() {
        this.getFormValidator().validate(this.getValues()).then((errors) => {
            const submitButton = this.getFormElement() ? this.getFormElement().querySelector('button[type="submit"]') : null;
            if (!submitButton) {
                return;
            }
            if (!errors) {
                submitButton.classList.add("px-submit");
                submitButton.classList.remove("px-submit-disabled");
            }
            else {
                submitButton.classList.remove("px-submit");
                submitButton.classList.add("px-submit-disabled");
            }
        });
    }
    /**
     * Disable form elements.
     */
    disableForm() {
        for (let i = 0, element; element = this.getFormElement().elements[i++];) {
            if (!element.hasAttribute('disabled')) {
                element.setAttribute('data-px-form-disabled', true);
                element.setAttribute('disabled', true);
            }
        }
    }
    /**
     * Enable form elements.
     */
    enableForm() {
        for (let i = 0, element; element = this.getFormElement().elements[i++];) {
            if (element.hasAttribute('data-px-form-disabled') && !element.hasAttribute(DataAttributes_1.DataAttributes.INPUT_DISABLED)) {
                element.removeAttribute('disabled');
                element.removeAttribute('data-px-form-disabled');
            }
        }
    }
    /**
     * Gets the form values.
     *
     * @TODO: Return type of the values should be definable (generics) or fixed type.
     * @return {{}}
     */
    getValues() {
        return new FormData(this.getFormElement());
    }
    /**
     * Gets the form values.
     *
     * @TODO: Getting values should be extracted into a different service.
     * @return {{}}
     */
    getValue(inputName) {
        return this.getValues().get(inputName);
    }
    /**
     * Validates the form.
     *
     * @param values
     * @param action
     * @return {Promise<boolean>}
     */
    validate(values, action = "default") {
        return this.getFormValidationRenderer().removeForAll()
            .then(() => this.getFormValidator().validate(values))
            .then((errors) => {
            if (!errors) {
                return true;
            }
            return this.getFormValidationRenderer().display(errors)
                .then(() => this.scrollToFirstError())
                .then(() => false);
        })
            .catch((error) => this.emit(FormHandlerEvent_1.FormHandlerEvent.ERROR, error) && false);
    }
    /**
     * Validates a single field.
     *
     * @param field
     * @param value
     */
    validateField(field, value) {
        return this.getFormValidationRenderer().removeForField(field)
            .then(() => this.getFormValidator().validateField(field, value))
            .then((errors) => {
            if (!errors) {
                return true;
            }
            return this.getFormValidationRenderer().displayForField(field, errors)
                .then(() => this.scrollToFirstError())
                .then(() => false);
        })
            .catch((error) => this.emit(FormHandlerEvent_1.FormHandlerEvent.ERROR, error) && false);
    }
    /**
     * Submits the form and extracts the data.
     *
     * @param values
     * @param action
     * @return {Promise<*>}
     */
    submit(values, action = "default") {
        return Promise.resolve({});
    }
    /**
     * Controls the submission of the form.
     */
    doSubmit() {
        // Make sure form is enabled before querying values as otherwise the form data
        // will not contain the field values.
        this.enableForm();
        const formElement = this.getFormElement();
        const values = this.getValues();
        const action = formElement.getAttribute("data-px-form-submit-action") || "default";
        this.emit(FormHandlerEvent_1.FormHandlerEvent.VALIDATING, values, action);
        this.state = FormStates_1.FormStates.VALIDATING;
        return this.validate(values, action)
            .then((valid) => {
            if (!valid) {
                this.emit(FormHandlerEvent_1.FormHandlerEvent.VALIDATION_FAIL, values, action);
                this.state = FormStates_1.FormStates.EDITING;
                return;
            }
            this.disableForm();
            this.emit(FormHandlerEvent_1.FormHandlerEvent.SUBMITTING, values, action);
            this.state = FormStates_1.FormStates.SUBMITTING;
            return this.submit(values, action)
                .then((data) => {
                let promises = [];
                this.listeners(FormHandlerEvent_1.FormHandlerEvent.SUBMITTED).forEach((callback) => {
                    promises.push(callback(data, action));
                });
                promises = promises.filter((promise) => promise instanceof Promise);
                return Promise.all(promises);
            })
                .catch((error) => {
                this.emit(FormHandlerEvent_1.FormHandlerEvent.ERROR, error);
            })
                .then(() => {
                this.state = FormStates_1.FormStates.EDITING;
                if (this.getFormElement()) {
                    this.enableForm();
                    this.validateFormAndDisplaySubmit();
                }
            })
                .catch(console.error);
        });
    }
}
exports.default = FormHandlerPluginBase;
Mixin_1.default(FormHandlerPluginBase, EventEmitter, IContainerInjection_1.default);
